/**
 * facet-preview-counts.js
 *
 * Problem: facets.liquid / facet-value-count.liquid compute stock-aware
 * filter counts from `results.products`, which is only ever the current
 * PAGE's worth of products. That's exact once a filter combination is
 * actually applied and happens to fit on one page, but it silently
 * under-counts:
 *   - the overall "X products" total for the current combination, if that
 *     combination's true matches span more than one page
 *   - the count next to EVERY checkbox (checked or not), whenever the true
 *     matches for that value span more than one page
 * This is what caused numbers to visibly jump between "before you click" and
 * "after you click" - not a logic bug, a data-visibility limitation.
 *
 * Fix: fetch the full product catalog for the current collection from a
 * Cloudflare Worker (lucyandyak-facet-catalog.lucy-and-yak.workers.dev),
 * which maintains a KV-backed cache refreshed every 5 minutes. A single
 * fetch per tab session replaces the previous multi-page Section Rendering
 * API crawl entirely. Every count on the page is then computed in memory
 * from that dataset using the same per-variant/per-product matching rules
 * used server-side.
 *
 * Safeguards:
 *   - sessionStorage caches the Worker response by collection handle,
 *     stamped with a fetch time and reused for up to CATALOG_TTL_MS
 *     (5 minutes - deliberately matched to the Worker's own KV refresh
 *     cadence, so the client can never be staler than one Worker cycle
 *     behind). Within that window every page load / filter click reuses
 *     it instantly; after it, the next use refetches. Previously this
 *     cache had NO expiry, so a long-lived tab kept showing counts from
 *     whenever the session started, drifting from reality as sale stock
 *     moved (observed live: a size count showing 15 against a true 12).
 *   - If the Worker fetch fails (even after retries), nothing is cached
 *     and nothing on the page is updated - a temporary hiccup leaves the
 *     existing number in place rather than overwriting it with nothing.
 *
 * Known gaps:
 *   - Only understands the same three filter "kinds" as
 *     facet-value-count.liquid: variant option (Size, Fit - matched
 *     per-variant, with range-overlap for "UK "-style labels), variant
 *     metafield (e.g. Availability - matched per-variant), and product
 *     metafield (e.g. Colour - matched once per product), plus the
 *     synthetic "std:" keys for Vendor/Product Type (see detectKind).
 *   - Assumes multiple checked values within the SAME filter combine via OR.
 *   - Worker returns products scoped to the current collection handle via
 *     ?collection= query param; the Worker must store collection membership
 *     per product for this to work correctly.
 *
 * Bug fixed 2026-07-29: preview counts for an UNCHECKED checkbox (e.g.
 * previewing "Availability: Out of Stock" while "Size: Medium" and
 * "Colour: Black" are already active) were computed in two separate
 * passes - first narrowing the catalog to products matching the OTHER
 * active entries, then separately counting the candidate value against
 * that narrowed set. The Liquid reference this mirrors
 * (facet-value-count.liquid) requires ONE single variant to satisfy every
 * active/candidate entry simultaneously before that variant (and its
 * product) counts. Splitting into two passes silently drops that
 * constraint whenever more than one variant-level entry (option or vmf) is
 * in play: a product could pass the "eligible" narrowing because SOME
 * variant matched Size=Medium, and separately pass the candidate check
 * because a DIFFERENT variant happened to carry the previewed vmf value -
 * even though no single variant was both. That produced non-zero preview
 * counts (e.g. "Out of Stock (80)") for combinations that matched zero
 * products once actually selected. Fixed by combining every variant-level
 * entry (other active ones plus the candidate) into a single matching
 * pass; only product-level (pmf) entries are still used to pre-filter
 * independently, since they don't depend on variant identity.
 *
 * Bug fixed 2026-08-12: pmfValueMatches() checked a LIST-type metafield
 * (e.g. custom.product_type, stored as an array like ["Underwear Bottoms"])
 * for an EXACT element match only, while the plain-STRING case two lines
 * below already allowed a substring/"contains" match. That asymmetry was
 * invisible for most Product Type values (their label text and their
 * literal metafield value are identical strings, e.g. "Dresses" ==
 * "Dresses"), but broke as soon as a checkbox's label didn't equal the
 * underlying literal value one-for-one - which is exactly what happens for
 * a Shopify-standardized/taxonomy filter value: the "Underwear" checkbox is
 * rendered with a FilterSettingGroup GID as its `value` (not a plain
 * string), grouping together the store's two real literal values,
 * "Underwear Bottoms" and "Underwear Tops". Neither literal string equals
 * "Underwear" exactly, so the array branch found zero matches, computed
 * totalCount = 0 for the checked box, and overwrote Liquid's correct
 * server-rendered count (16, matching the grid and Shopify's own native
 * filter.values[].count) with that wrong 0 - for both the checkbox itself
 * and the overall "X products" summary, since both are derived from the
 * same totalCount. Fixed by extending the array branch to do the same
 * substring check per element that the string branch already does, so
 * "Underwear" now matches "Underwear Bottoms" / "Underwear Tops" the same
 * way it would match a plain string containing "Underwear". Checked the
 * full current Product Type value list for this collection (32 values) -
 * none of them is a substring of a different value, so this doesn't
 * introduce any new cross-matches; it only fixes the case that was already
 * silently broken.
 */
(function () {

  var WORKER_BASE_URL = 'https://lucyandyak-facet-catalog.lucy-and-yak.workers.dev';

  // Extract the current collection handle from the URL path,
  // e.g. /collections/dungarees -> "dungarees"
  function collectionHandle() {
    var match = window.location.pathname.match(/\/collections\/([^\/\?]+)/);
    return match ? match[1] : null;
  }

  // Feature added 2026-08-17: search results (/search?q=...) never had ANY
  // scoping at all before this - collectionHandle() returns null there (no
  // /collections/ in the path), so workerUrl() fell through to an entirely
  // unscoped request, and the script wasn't even loaded on this template to
  // begin with (confirmed live: zero <script src="facet-preview-counts.js">
  // tags on a real /search page - see sections/main-search.liquid, which now
  // adds it). Reads the `q` param directly from the URL rather than needing
  // any new Liquid-rendered data attribute - the query is already right there
  // client-side.
  function searchQuery() {
    if (!/(^|\/)search\/?$/.test(window.location.pathname)) return null;
    var params = new URLSearchParams(window.location.search);
    var q = params.get('q');
    return q && q.trim() ? q.trim() : null;
  }

  function workerUrl() {
    var handle = collectionHandle();
    if (handle) return WORKER_BASE_URL + '?collection=' + encodeURIComponent(handle);
    var q = searchQuery();
    if (q) return WORKER_BASE_URL + '?q=' + encodeURIComponent(q);
    return WORKER_BASE_URL;
  }

  // ---- Matching engine (mirrors facet-value-count.liquid) ----

  // Bug fixed 2026-07-30: this used to check `indexOf('UK ') !== -1` -
  // "does this string contain 'UK ' anywhere" - then parsed whatever number
  // followed it. That made a denim waist size like "W32 (UK 14)" parse to
  // the same [14,14] range as a plain "UK 14", so checking the Size:UK14
  // facet also silently pulled in W32 waist-size variants that reference
  // UK14 only as a conversion aid in parentheses. Shopify's own native
  // filtering treats these as distinct raw values (that's why the grid and
  // the Size checkbox's own untouched count stayed correct at 18 while this
  // script's shared total came out at 23 - the +5 was exactly the W32 rows).
  //
  // Fix is scoped narrowly to the "W<number> (...)" waist-size pattern only
  // (confirmed via the full 51-value raw size list for this catalog: the
  // only other "<prefix> (UK ...)" formats are letter sizes like
  // "M (UK 12-14)", which are ranges and never collide with a single-value
  // checkbox like "UK 14" anyway, since matching below requires the full
  // range to match exactly, not just overlap). An earlier, broader "must
  // start with UK" version of this fix was reverted because it would have
  // also stopped matching any size format that doesn't literally lead with
  // "UK " - too blunt for a 51-value catalog we hadn't actually inspected yet.
  function parseUkRange(text) {
    if (!text) return null;
    var str = String(text).trim();
    if (/^W\d+\s*\(/i.test(str)) return null;
    if (str.indexOf('UK ') === -1) return null;
    var after = str.split('UK ').pop().replace(')', '').trim();
    var parts = after.split('-').map(function (s) {
      return parseInt(s.trim(), 10);
    });
    if (isNaN(parts[0])) return null;
    var low = parts[0];
    var high = parts.length > 1 && !isNaN(parts[1]) ? parts[1] : low;
    return [low, high];
  }

  // Bug fixed 2026-08-13 (2): a raw value that is NOTHING but "UK <n>" or
  // "UK <n>-<n>" - no letter-size prefix (L/M/S/XL/XS/XXS/...), no W-prefix -
  // is a genuinely distinct sizing convention elsewhere in this catalog
  // (confirmed via the live Worker catalog: bare "UK 16" exists as its own
  // raw Size value, 19 products, entirely separate from the letter-size
  // "L (UK 16-18)" value, 4 products - almost certainly a different garment
  // type, e.g. dresses/skirts measured in plain UK numbers rather than
  // trouser-style S/M/L). The 2026-08-13 fuzzy-containment fix above (added
  // to let "XS (UK 6-8)" match its real literal member "XS (UK 6)") checked
  // ranges purely numerically, so it also let bare "UK 16" satisfy "Large
  // (UK 16-18)" just because 16 falls inside [16,18] - inflating that
  // checkbox from the correct 4 to 23 (4 real + 19 unrelated bare-UK
  // variants). A bare UK value must never satisfy a grouped/fuzzy candidate
  // through containment; it falls back to the ordinary exact-range check
  // instead (same rule every non-grouped candidate already uses).
  function isBareUkValue(str) {
    return /^UK\s*\d+(\s*-\s*\d+)?$/i.test(String(str).trim());
  }

  var SIZE_GROUP_EXTRAS = { 'uk 4': ['w22 (uk 4)'] };

  function valuesMatch(entry, actualValue) {
    if (actualValue === undefined || actualValue === null) return false;
    var entryValue = typeof entry === 'string' ? entry : entry.label;
    var fuzzy = typeof entry === 'string' ? false : !!entry.fuzzy;
    // Feature added 2026-10-12: Shopify's own merged Size groups (Search &
    // Discovery) fold some other raw values into a checkbox. Derived live by
    // comparing each Size checkbox's real grid against in-stock sizes; the
    // only cross-family merge found is "UK 4" including "W22 (UK 4)". The
    // same rule lives in main-collection-product-grid.liquid - keep in sync.
    var extras = SIZE_GROUP_EXTRAS[String(entryValue).toLowerCase()];
    if (extras && extras.indexOf(String(actualValue).toLowerCase()) !== -1) return true;
    if (String(entryValue).indexOf('UK ') !== -1) {
      var actualRange = parseUkRange(actualValue);
      var entryRange = parseUkRange(entryValue);
      if (actualRange && entryRange) {
        // Bug fixed 2026-10-12: a lettered checkbox like "XXS (UK 4)" must
        // never match a bare "UK 4" variant. The exact-range fallback below
        // treated [4,4] == [4,4] as a match, so XXS (UK 4) showed 21 (3 real
        // + 18 bare "UK 4") against 3 real products on the grid.
        // Applies in BOTH directions: a bare "UK 32" checkbox must not match
        // "4XL (UK 32)" variants either (showed 8 = 3 + 5 against 3 cards).
        if (isBareUkValue(entryValue) !== isBareUkValue(actualValue)) return false;
        if (fuzzy && !isBareUkValue(actualValue)) {
          // Bug fixed 2026-08-13: a FilterSettingGroup-grouped Size checkbox
          // (confirmed via its `value` attribute, a
          // gid://shopify/FilterSettingGroup/... GID - same mechanism as the
          // 2026-08-12 Underwear/pmf bug, just on a variant OPTION filter
          // this time) can display a combined RANGE label (e.g.
          // "XS (UK 6-8)") that no single literal variant value actually
          // equals. Checked the live Worker catalog for this store: there is
          // no "XS (UK 6-8)" value anywhere, only the narrower literal
          // "XS (UK 6)" - which the old exact-range-equality check rejected
          // outright ([6,6] != [6,8]), showing (0) for a box the grid (and
          // Shopify's own native filtering) proves has real matches.
          // "XXS (UK 4)" is ALSO GID-grouped but happened to keep working,
          // because its group's literal value coincidentally equals its
          // display label one-for-one ([4,4] == [4,4]).
          // Group membership (exactly which literal values Shopify folds
          // into a given group) isn't exposed to this script - but treating
          // a variant's own UK number(s) as a match whenever they fall
          // entirely WITHIN the group's displayed range is a safe, narrow
          // approximation: it can only include values that already fall
          // inside what the checkbox's own label claims, and can never reach
          // into a neighbouring size's territory (e.g. this can't make
          // "UK 10" match "XS (UK 6-8)"). Only applied when fuzzy is true;
          // every non-grouped candidate keeps the exact-equality rule below,
          // completely unchanged.
          return actualRange[0] >= entryRange[0] && actualRange[1] <= entryRange[1];
        }
        return entryRange[0] === actualRange[0] && entryRange[1] === actualRange[1];
      }
      // Bug fixed 2026-08-12 (5): parseUkRange() deliberately returns null
      // for a W-prefixed denim waist size (e.g. "W28 (UK 10)") - that's the
      // 2026-07-30 fix preventing a W-size from being treated as equivalent
      // to an unrelated plain UK-range size that happens to share the same
      // UK number (that's what let "W32 (UK 14)" wrongly pull in the "UK
      // 14" checkbox's results). But this branch never accounted for what
      // to do with that null - it always returned false whenever EITHER
      // side was W-prefixed, even when comparing a W-size candidate
      // against a variant whose own option value is that exact same
      // W-prefixed string (the ordinary case: checking "W28 (UK 10)"
      // should match a variant literally labelled "W28 (UK 10)"). That
      // silently broke every W-size checkbox, checked or not - every one
      // showed 0 regardless of how many products actually had that size.
      // Falling back to a plain exact string comparison here, instead of
      // unconditional false, fixes that without reopening the
      // cross-family collision the null guard exists to prevent (a W-size
      // string and a plain UK-range string are never textually identical,
      // so this fallback can't accidentally match them to each other).
      return String(entryValue).toLowerCase() === String(actualValue).toLowerCase();
    }
    return String(entryValue).toLowerCase() === String(actualValue).toLowerCase();
  }

  // A checkbox's raw filter `value` is a Shopify FilterSettingGroup GID only
  // when it's a standardized/taxonomy filter value grouping multiple real
  // literal values under one display label (e.g. "Underwear" grouping the
  // store's actual "Underwear Bottoms" / "Underwear Tops" metafield values).
  // A plain literal value (e.g. "Shorts") is never a GID.
  function isGroupedFilterValue(rawValue) {
    return typeof rawValue === 'string' && rawValue.indexOf('gid://shopify/FilterSettingGroup/') === 0;
  }

  // `values` is an array of `{ label, fuzzy }` descriptors (every kind, as
  // of the 2026-08-13 fix - previously option/vmf stayed plain label
  // strings, since only pmf checkboxes were known to render a
  // FilterSettingGroup GID `value`) - see isGroupedFilterValue() above and
  // its call sites in activeEntriesFromCheckboxes() and run().
  function pmfValueMatches(mfValue, values) {
    if (mfValue === undefined || mfValue === null) return false;
    return values.some(function (entry) {
      var v = typeof entry === 'string' ? entry : entry.label;
      var fuzzy = typeof entry === 'string' ? false : !!entry.fuzzy;
      if (mfValue === v) return true;
      if (Array.isArray(mfValue)) {
        // Bug fixed 2026-08-12 (3): the substring/"contains" leniency added
        // just below to fix Underwear (see the 2026-08-12 fix note in the
        // file header) was applied unconditionally, so it ALSO matched
        // "Shorts" against the literal value "Dungaree Shorts" - a real,
        // separate Product Type on this store, not a synonym of Shorts
        // (confirmed via the debug banner: displayed count was 46 = 39
        // Shorts + 7 Dungaree Shorts). Only allow the substring match for a
        // candidate whose OWN checkbox value was a FilterSettingGroup GID
        // (fuzzy === true) - that's the one case where the label genuinely
        // doesn't equal any single literal value one-for-one, so substring
        // is the best available heuristic. A plain-string checkbox value
        // like "Shorts" IS the literal value and must match it exactly.
        return mfValue.some(function (item) {
          if (item === v) return true;
          return fuzzy && typeof item === 'string' && typeof v === 'string' && item.indexOf(v) !== -1;
        });
      }
      if (typeof mfValue === 'string') return mfValue.indexOf(v) !== -1;
      return String(mfValue) === String(v);
    });
  }

  function productMatchesEntries(product, entries) {
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      if (e.kind === 'pmf') {
        var mfValue = product.pmf ? product.pmf[e.key] : undefined;
        if (!pmfValueMatches(mfValue, e.values)) return false;
      }
    }
    var variantEntries = entries.filter(function (e) {
      return e.kind === 'option' || e.kind === 'vmf';
    });
    var variants = product.variants || [];
    return variants.some(function (variant) {
      if (!variant.available) return false;
      return variantEntries.every(function (e) {
        var actual = e.kind === 'option' ? (variant.option || {})[e.key] : (variant.vmf || {})[e.key];
        if (actual === undefined || actual === null) return false;
        return e.values.some(function (v) {
          return valuesMatch(v, actual);
        });
      });
    });
  }

  function countMatches(products, entries) {
    var count = 0;
    products.forEach(function (p) {
      if (productMatchesEntries(p, entries)) count += 1;
    });
    return count;
  }

  // ---- Fetching the catalog from the Worker ----

  var MAX_RETRIES = 6;

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  async function fetchWithRetry(url) {
    for (var attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      var response;
      try {
        response = await fetch(url);
      } catch (e) {
        response = null;
      }

      if (response && response.ok) return response;

      var isRateLimited = response && response.status === 429;
      var isRetryableServerError = response && response.status >= 500;
      var isNetworkFailure = !response;

      if (attempt === MAX_RETRIES || !(isRateLimited || isRetryableServerError || isNetworkFailure)) {
        return response;
      }

      var retryAfterHeader = response && response.headers ? response.headers.get('Retry-After') : null;
      var retryAfterMs = retryAfterHeader ? parseFloat(retryAfterHeader) * 1000 : null;
      var backoffMs = retryAfterMs || Math.min(300 * Math.pow(2, attempt), 8000);
      backoffMs += Math.random() * 200;
      await delay(backoffMs);
    }
  }

  function catalogHasExpectedShape(catalog, expectedPmfKeys) {
    if (!expectedPmfKeys.length) return true;
    if (!catalog.length) return true;
    var sample = catalog[0];
    return expectedPmfKeys.every(function (key) {
      return sample.pmf && Object.prototype.hasOwnProperty.call(sample.pmf, key);
    });
  }

  var catalogPromise = null;

  // How long a fetched catalog stays trusted, in BOTH caching layers below
  // (the in-page catalogPromise memo and the sessionStorage entry). Matched
  // to the Worker's KV refresh interval (5 minutes - see worker/index.js),
  // so the numbers on screen are never more than one Worker cycle behind
  // live stock. Raising this saves nothing (the Worker answers from KV in
  // one cheap request); lowering it below the Worker's cadence just
  // refetches identical data.
  var CATALOG_TTL_MS = 5 * 60 * 1000;
  var catalogFetchedAt = 0;

  function fetchFullCatalog(sectionId, expectedPmfKeys) {
    // The in-page memo must expire too, not just sessionStorage: filter
    // clicks re-render via the Section Rendering API without a page
    // navigation, so a long-lived tab would otherwise keep reusing this
    // promise forever and never consult sessionStorage (or the Worker)
    // again.
    if (catalogPromise && Date.now() - catalogFetchedAt < CATALOG_TTL_MS) return catalogPromise;
    catalogPromise = null;
    catalogFetchedAt = Date.now();

    catalogPromise = (async function () {
      var handle = collectionHandle();
      // Feature added 2026-08-17: give a search-query request its own cache
      // key (distinct from both a real collection handle and the generic
      // "_all" fallback), so a "red" search and, say, an actual collection
      // literally named "red" (or the truly-unscoped "_all" case) can never
      // collide in sessionStorage.
      var q = !handle ? searchQuery() : null;
      var cacheKey = 'facetFullCatalog:worker:' + (handle || (q ? 'search:' + q : '_all'));

      try {
        var cached = sessionStorage.getItem(cacheKey);
        if (cached != null) {
          var parsedEnvelope = JSON.parse(cached);
          // Entries are stored as { v: 1, t: <epoch ms>, products: [...] }.
          // A legacy entry from before the TTL change is a bare array -
          // parsedEnvelope.products comes back undefined for those, so they
          // fail the checks below and get refetched/overwritten in the new
          // format. No migration needed.
          var parsedCache = parsedEnvelope && parsedEnvelope.products;
          var isFresh = parsedEnvelope
            && typeof parsedEnvelope.t === 'number'
            && Date.now() - parsedEnvelope.t < CATALOG_TTL_MS;
          if (
            isFresh
            && Array.isArray(parsedCache)
            && catalogHasExpectedShape(parsedCache, expectedPmfKeys)
          ) {
            return parsedCache;
          }
          // Expired, legacy bare-array format, or stale shape - fall
          // through and refetch.
        }
      } catch (e) {
        /* sessionStorage unavailable or corrupt - just refetch */
      }

      // Bug fixed 2026-08-17: the Worker replies to the VERY FIRST request for
      // any brand-new scope (a search query nobody has ever searched before,
      // or - much more rarely - a genuinely new collection) with a deliberate
      // "cold" placeholder: HTTP 200, body `[]`, while it resolves the real
      // answer in the background (see resolveUncachedSearch/
      // resolveUncachedCollection in worker/index.js - never blocks the
      // storefront on a live Shopify call). This code used to treat ANY
      // empty-array response as equivalent to a hard failure (network error,
      // bad JSON) and just returned null - which happened to be harmless for
      // collections (almost always already warm) but hits on EVERY single
      // first-time search query, since a distinct query string is "new" until
      // resolved. That's exactly what live SEARCHDEBUG traces showed on
      // /search?q=purple: `catalog resolved, length= null` on the very first
      // load, run() bailing before patching anything, leaving Shopify's own
      // uncorrected native counts on screen.
      //
      // The `X-Catalog-Cache` response header (added alongside this fix's
      // Access-Control-Expose-Headers change in worker/index.js's
      // corsHeaders() - without that change this header silently reads back
      // as null here, since it's a cross-origin request and custom headers
      // aren't exposed to JS by default) tells us definitively which case
      // we're in: "cold" is always the empty placeholder; "hit"/"miss" mean
      // the Worker actually resolved this scope, so an empty array there is a
      // genuine (if perhaps unusual) zero-match result, not a not-ready-yet
      // signal, and should be used like any other catalog.
      //
      // Bug fixed 2026-08-17 (2): distinguishing "cold" from "hit"/"miss"
      // alone was NOT enough - confirmed live via a wrangler tail on a real,
      // never-before-searched "orange" query: the client's ONE AND ONLY
      // fetch for that page load landed while the Worker was still cold
      // ("orange" not yet resolved - resolving in background), and nothing
      // afterwards ever asked the Worker again, even though the background
      // resolve itself finished about 2 seconds later ("background resolve
      // done for search 'orange' - 58 product(s) cached") - so the very first
      // fix change (return null on cold) was correct as far as it went, but
      // still just gave up and left Shopify's uncorrected native numbers on
      // screen for that entire page view, with nothing left to trigger a
      // retry unless the shopper happened to reload again later or click a
      // filter. Every wrangler tail sample of a background resolve seen so
      // far (this "orange" query, plus earlier "red"/"green"/"grey"/"gray")
      // completed within about 2 seconds - a single Storefront `search` GraphQL
      // call. So instead of giving up on the first cold response, retry a
      // bounded number of times with short waits while the resolve is
      // in-flight; only fall back to leaving the page uncorrected (return
      // null, same safe behavior as before) if it's STILL cold after this
      // whole budget, e.g. an unusually slow/throttled Shopify search call.
      var COLD_RETRY_DELAYS_MS = [500, 1000, 1500, 2000]; // ~5s total, comfortably above the ~2s resolves seen live; last resort only

      var response, cacheState, allProducts;
      for (var coldAttempt = 0; ; coldAttempt++) {
        response = await fetchWithRetry(workerUrl());
        if (!response || !response.ok) return null;

        cacheState = response.headers.get('X-Catalog-Cache');

        try {
          allProducts = await response.json();
        } catch (e) {
          return null;
        }

        if (!Array.isArray(allProducts)) return null;

        var isColdPlaceholder = allProducts.length === 0 && cacheState !== 'hit' && cacheState !== 'miss';
        if (!isColdPlaceholder) break;
        if (coldAttempt >= COLD_RETRY_DELAYS_MS.length) return null; // exhausted the retry budget - give up gracefully, same as before
        await delay(COLD_RETRY_DELAYS_MS[coldAttempt]);
      }

      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({ v: 1, t: Date.now(), products: allProducts })
        );
      } catch (e) {
        /* storage full/unavailable */
      }

      return allProducts;
    })();

    catalogPromise.then(function (result) {
      if (!result) catalogPromise = null;
    });

    return catalogPromise;
  }

  // ---- Reading filter checkboxes out of the rendered facets ----

  function detectKind(paramName) {
    if (paramName.indexOf('option.') !== -1) {
      return { kind: 'option', key: paramName.split('option.').pop() };
    }
    if (paramName.indexOf('v.m.') !== -1) {
      return { kind: 'vmf', key: paramName.split('v.m.').pop() };
    }
    if (paramName.indexOf('p.m.') !== -1) {
      return { kind: 'pmf', key: paramName.split('p.m.').pop() };
    }
    if (paramName === 'filter.p.vendor') {
      return { kind: 'pmf', key: 'std:vendor' };
    }
    if (paramName === 'filter.p.product_type') {
      return { kind: 'pmf', key: 'std:type' };
    }
    return { kind: null, key: null };
  }

  function getLabelText(checkbox) {
    var label = checkbox.closest('label');
    if (!label && checkbox.id) label = document.querySelector('label[for="' + checkbox.id + '"]');
    if (!label) return checkbox.value;
    var span = label.querySelector('.facet-checkbox__text-label');
    return span ? span.textContent.trim() : label.textContent.trim();
  }

  function collectCheckboxes() {
    var map = {};
    Array.from(
      document.querySelectorAll('#FacetFiltersForm input[type="checkbox"], #FacetFiltersFormMobile input[type="checkbox"]')
    ).forEach(function (cb) {
      if (!cb.name) return;
      var info = detectKind(cb.name);
      if (!info.kind) return;
      var key = cb.name + '::' + cb.value;
      if (!map[key]) {
        map[key] = {
          name: cb.name,
          value: cb.value,
          label: getLabelText(cb),
          kind: info.kind,
          entryKey: info.key,
          checked: false,
          elements: []
        };
      }
      map[key].elements.push(cb);
      if (cb.checked) map[key].checked = true;
    });
    return map;
  }

  function activeEntriesFromCheckboxes(map) {
    var groups = {};
    Object.keys(map).forEach(function (k) {
      var item = map[k];
      if (!item.checked) return;
      var groupKey = item.kind + '::' + item.entryKey;
      if (!groups[groupKey]) groups[groupKey] = { kind: item.kind, key: item.entryKey, values: [] };
      // Bug fixed 2026-08-13: every kind now carries the fuzzy flag (was
      // pmf-only before) - pmfValueMatches() has needed it since the
      // 2026-08-12 (3) fix, and valuesMatch() needs it too now for
      // GID-grouped option/vmf values like the "XS (UK 6-8)" Size checkbox
      // (see the 2026-08-13 fix note on valuesMatch()).
      groups[groupKey].values.push({ label: item.label, fuzzy: isGroupedFilterValue(item.value) });
    });
    // Feature added 2026-10-01 (see discount-filter-live.js): the Discount
    // values in the URL are the source of truth, not the ticked checkboxes.
    // Shopify's checkbox list lags behind live discount_percentage values,
    // so e.g. a min_discount=50 link sends 50..100 but only the values that
    // happen to have a checkbox were being counted (81 shown as the total
    // while 109 cards were on the grid).
    var urlDiscountValues = new URLSearchParams(window.location.search).getAll('filter.p.m.custom.discount_percentage');
    if (urlDiscountValues.length) {
      groups['pmf::custom.discount_percentage'] = {
        kind: 'pmf',
        key: 'custom.discount_percentage',
        values: urlDiscountValues.map(function (v) {
          return { label: String(parseInt(v, 10)), fuzzy: false };
        })
      };
    }
    return Object.keys(groups).map(function (k) {
      return groups[k];
    });
  }

  // ---- Patching the DOM ----

  function replaceLastNumber(text, newValue) {
    var matches = text.match(/[\d,]+/g);
    if (!matches) return text;
    var last = matches[matches.length - 1];
    var lastIndex = text.lastIndexOf(last);
    return text.slice(0, lastIndex) + newValue + text.slice(lastIndex + last.length);
  }

  function updateTrailingNumber(container, newValue) {
    if (!container) return;
    for (var i = container.childNodes.length - 1; i >= 0; i--) {
      var node = container.childNodes[i];
      if (node.nodeType === Node.TEXT_NODE && /\d/.test(node.textContent)) {
        // Same category of fix as updateOverallCount() below: only touch
        // the Text node's data if the value actually changes, so a
        // no-op patch pass never fires a mutation of its own.
        var updated = replaceLastNumber(node.textContent, newValue);
        if (updated !== node.textContent) node.textContent = updated;
        return;
      }
    }
    // Fallback path (no existing text-node child found): assigning
    // `.textContent` here is on the ELEMENT itself, which always replaces
    // its children wholesale - a childList mutation, exactly the same
    // self-triggering-observer risk fixed in updateOverallCount() below.
    // Guarding this the same way (skip the write when nothing changes)
    // closes that same class of bug for any checkbox count element that
    // happens to hit this fallback.
    if (/\d/.test(container.textContent)) {
      var next = replaceLastNumber(container.textContent, newValue);
      if (next !== container.textContent) container.textContent = next;
    }
  }

  // Some facet blocks (e.g. grouped/size-chart filters, whose checkbox
  // `value` is a FilterSettingGroup GID rather than a plain option string)
  // render with `<label for="...">` pointing at the checkbox instead of
  // wrapping it. `cb.closest('label')` misses those, so counts for that
  // facet were silently never patched - the box just kept whatever number
  // the theme originally rendered, which is why it could disagree with
  // every other (correctly-patched) facet. getLabelText() already had this
  // fallback; the count-updating paths below did not.
  function findLabel(cb) {
    var label = cb.closest('label');
    if (label) return label;
    if (cb.id) return document.querySelector('label[for="' + cb.id + '"]');
    return null;
  }

  function updateCheckboxCount(item, count) {
    item.elements.forEach(function (cb) {
      var label = findLabel(cb);
      if (!label) return;
      updateTrailingNumber(label.querySelector('.facet-checkbox__text'), count);
      updateTrailingNumber(label.querySelector('.visually-hidden'), count);
    });
  }

  // Bug fixed 2026-08-13 (4): hide a filter dropdown row entirely once its
  // TRUE (catalog-corrected) count is 0 - checking it would produce zero
  // results, so there's no reason to keep offering it. Deliberately never
  // hides a row for a CHECKED box, even if that box's own count happens to
  // be 0: hiding an active filter out from under the person who applied it
  // would remove their only way to uncheck it. Checked per-element (not via
  // the aggregate `item.checked`) since the desktop/mobile copies of the
  // same value have occasionally been observed briefly out of sync (see the
  // 2026-08-13 (3) MutationObserver-scope fix above) - checking each
  // element's own `.checked` means a stray unpatched copy can never get
  // hidden while it's still the box actively representing an applied
  // filter. Uses its own dedicated class (not the theme's shared `hidden`
  // utility) so this can never fight with the pre-existing "show more"
  // toggle, which already uses `hidden` on `show-more-item` rows for an
  // unrelated reason - reusing that class here would un-hide a row that's
  // supposed to stay collapsed until "show more" is clicked, the moment its
  // count happened to be patched to something non-zero.
  var ZERO_COUNT_CLASS = 'facet-zero-count-hidden';

  function findRow(cb) {
    return cb.closest('.facets__item, .mobile-facets__item');
  }

  function setZeroCountVisibility(item, count) {
    item.elements.forEach(function (cb) {
      var row = findRow(cb);
      if (!row) return;
      row.classList.toggle(ZERO_COUNT_CLASS, count === 0 && !cb.checked);
    });
  }

  // Bug fixed 2026-08-13 (7): this used to reassign `el.textContent`
  // unconditionally, every single run() - even when the replacement
  // produces the exact same string as what was already there. Assigning
  // `.textContent` on an ELEMENT (as opposed to a Text node) always
  // replaces its children wholesale, which is a childList mutation
  // regardless of whether the resulting text actually differs. All 3
  // matched elements (#ProductCount, #ProductCountDesktop,
  // .mobile-facets__count) live inside the subtrees the MutationObserver
  // below watches (#ProductGridContainer / #main-collection-filters), so
  // every run() retriggered that same observer, which scheduled another
  // run(), which mutated the same 3 elements again - a self-sustaining
  // infinite loop that never settled. Confirmed live via debug
  // instrumentation: the observer fired with exactly 3 mutations in its
  // batch on every single cycle, forever (9+ generations in under a
  // second, still climbing). The full-page loading overlay does clear at
  // the end of every one of those cycles, but gets shown again by the very
  // next self-triggered cycle a moment later - indistinguishable from
  // "stuck" to anyone watching the page. Skipping the write whenever the
  // value hasn't changed breaks the loop at its source: once a value is
  // already correct, re-computing and reconfirming it no longer touches
  // the DOM, so no mutation fires, so no further run() gets scheduled.
  function updateOverallCount(totalVisible) {
    document.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count').forEach(function (el) {
      var next = el.textContent.replace(/^\s*[\d,]+/, function (match) {
        return match.replace(/[\d,]+/, String(totalVisible));
      });
      if (next !== el.textContent) el.textContent = next;
    });
  }

  // ---- Pending/spinner/overlay UI ----

  var PENDING_CLASS = 'facet-count-pending';
  var SPINNER_CLASS = 'facet-count-spinner';
  var PAGE_OVERLAY_ID = 'facet-page-loading-overlay';

  (function injectPendingStyle() {
    var style = document.createElement('style');
    style.textContent =
      '.' + PENDING_CLASS + '{opacity:.45;transition:opacity .15s ease;}' +
      '.' + SPINNER_CLASS + '{display:inline-block;width:9px;height:9px;margin-left:5px;' +
      'border:2px solid rgba(0,0,0,.15);border-top-color:rgba(0,0,0,.55);border-radius:50%;' +
      'vertical-align:middle;animation:facetCountSpin .6s linear infinite;}' +
      '@keyframes facetCountSpin{to{transform:rotate(360deg);}}' +
      '.loader{--color-1:#FAA3C0;--size:1px;width:calc(48 * var(--size));' +
      'height:calc(48 * var(--size));border:calc(5 * var(--size)) solid;' +
      'border-color:var(--color-1) transparent;border-radius:50%;' +
      'display:inline-block;box-sizing:border-box;animation:rotation 1s linear infinite;}' +
      '@keyframes rotation{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}' +
      '.' + ZERO_COUNT_CLASS + '{display:none !important;}' +
      // Feature added 2026-08-13 (9): Shopify's own native `.loading-overlay`
      // (rendered in main-collection-product-grid.liquid, toggled on by
      // facets.js adding a `.loading` class to `#ProductGridContainer
      // .collection` during its own AJAX section re-render) is redundant
      // now that this script shows its own full-page overlay/spinner for
      // the same moments. Deliberately only hides the overlay div itself,
      // not the `.loading` class or anything else it might also control
      // (e.g. dimming the grid, disabling pointer events during reload) -
      // those native effects are untouched; this just prevents Shopify's
      // OWN shimmer overlay from also appearing alongside ours.
      '.loading-overlay{display:none !important;}';
    document.head.appendChild(style);
  })();

  function ensurePageOverlay() {
    var el = document.getElementById(PAGE_OVERLAY_ID);
    if (el) return el;
    el = document.createElement('div');
    el.id = PAGE_OVERLAY_ID;
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText =
      'position:fixed;top:0;left:0;right:0;bottom:0;z-index:;' +
      'display:flex;align-items:center;justify-content:center;' +
      'background:rgba(255,255,255,.55);opacity:0;pointer-events:none;' +
      'transition:opacity .15s ease;';
    var spinner = document.createElement('div');
    spinner.className = 'loader';
    // Bug fixed 2026-08-13 (8): the spinner never rendered because the
    // theme has a global CSS reset rule targeting `:empty` elements
    // (confirmed by the user directly) - `.loader` is a pure CSS-border
    // spinner with zero children, so it matched that selector and got
    // hidden outright, regardless of any opacity/z-index styling, since an
    // element selector + :empty pseudo-class outranks a single class
    // selector in specificity. Per the CSS spec, :empty only matches when
    // an element has NO child nodes at all - not even a whitespace-only
    // text node counts as "empty" (only comment/processing-instruction
    // nodes are excluded from that check). Appending a single space text
    // node gives it exactly one child, so :empty no longer matches, with
    // zero visual effect (it's just whitespace, and the spinner's ring is
    // entirely drawn via border-color/border-radius, not by anything
    // textual). Verified directly against a real browser engine: an empty
    // `.loader` computes `display:none` under a `.loader:empty{display:
    // none}` rule, while the same element with this text-node child
    // computes `display:block`.
    spinner.appendChild(document.createTextNode(' '));
    el.appendChild(spinner);
    document.body.appendChild(el);
    return el;
  }

  // Bug fixed 2026-08-13 (10): the overlay appeared to "load twice" - show,
  // hide, show again - on a single filter click. Root cause: Shopify's own
  // facets.js does NOT swap the grid and every filter group in one atomic
  // step - it rewrites the clicked group, the grid, and every OTHER filter
  // group as separate DOM operations (see the 2026-08-13 (3) fix note below
  // on the MutationObserver, which exists specifically because these land as
  // distinct mutations). When two of those land more than ~50ms apart, the
  // shared MutationObserver debounce (see `pending` below) fires run() twice
  // instead of once - the first run() finishes and calls hidePageOverlay()
  // via clearAllPending() while the second native re-render is still
  // in-flight, so the overlay drops and then has to pop back up a moment
  // later for the second cycle.
  //
  // Fixed with a generation-token scheme rather than a plain show/hide-count
  // (a naive increment-on-show/decrement-on-hide counter was tried and
  // reverted earlier - markAllPending()/markJustClicked()/run() all call
  // showPageOverlay() at slightly different points in a single click's
  // lifecycle, so paired counting either double-counted a single click, or
  // - if that was compensated for - left the overlay stuck open forever the
  // moment any call site's count didn't unwind to exactly zero). This scheme
  // never requires the counts to balance:
  //   - every showPageOverlay() call bumps `pageOverlayGeneration` and
  //     stamps `pageOverlayShownAt = Date.now()`, unconditionally - however
  //     many times it's called per click, it's always safe to call again.
  //   - every hidePageOverlay() call captures the generation NUMBER at the
  //     time it's called, waits a short minimum-visible + settle delay, then
  //     - and only then - actually hides, but ONLY if no newer
  //     showPageOverlay() call has happened since (i.e. the generation is
  //     still what it captured). If a newer show happened in the meantime,
  //     this hide call is stale and simply does nothing - the LATER cycle's
  //     own eventual hidePageOverlay() call is what will actually hide it,
  //     using its own, up-to-date generation snapshot.
  // This can't get stuck: as soon as filter changes stop happening, the last
  // hidePageOverlay() call's generation snapshot is guaranteed to match
  // (nothing bumps it afterwards), so it fires. It also can't hide too
  // early: a stale hide is simply ignored rather than needing to be
  // "cancelled" by exactly-matching bookkeeping elsewhere.
  var PAGE_OVERLAY_MIN_VISIBLE_MS = 250; // don't let the overlay flash for under ~1/4s even when the fetch was instant (cached)
  var PAGE_OVERLAY_SETTLE_MS = 300; // grace window to absorb a closely-following second, separately-timed re-render cycle
  var pageOverlayGeneration = 0;
  var pageOverlayShownAt = null;

  // Bug fixed 2026-08-13 (11): even with the generation-token bridge above
  // (fix 10, which correctly absorbs two native re-renders landing within
  // ~500ms of each other - confirmed via live debug logging, see below), the
  // overlay still visibly dropped and popped back up on a real click. Live
  // instrumented console output (facet-preview-counts.DEBUG3.js) showed why:
  // a checkbox change triggers a FAST, harmless re-render almost immediately
  // (~700ms round trip - the same kind of active-filter-pill sync as
  // closeFilter()), which finishes and correctly hides the overlay per fix
  // 10's logic, since nothing else is pending AT THAT MOMENT. But
  // facets.js's own `debouncedOnSubmit` (this file, line ~8: `debounce(...,
  // 800)`) hasn't even fired yet - it waits 800ms after the triggering
  // `input` event before submitting the REAL filter change, which then does
  // an actual network fetch for the new grid section. In the live log, the
  // real grid-refreshing mutation didn't land until ~2.9s after the click -
  // no mutation, no pending work, nothing for this script to observe during
  // that multi-second gap, so the overlay (correctly, by its own local
  // logic) hid, then had to pop back up once the real refresh finally
  // arrived. That gap is far too long for PAGE_OVERLAY_SETTLE_MS to
  // reasonably bridge (300ms) without making EVERY click hold the overlay
  // open for seconds even when nothing else is coming.
  //
  // Fixed by pinning the overlay open from the moment of the click itself
  // (not from when any one re-render cycle happens to finish) through
  // Dawn's own known 800ms debounce plus a margin for the fetch that
  // follows it. `pageOverlayPinnedUntil` is a fixed point in time, set by
  // the checkbox 'change' listener; hidePageOverlay()'s scheduled check now
  // also refuses to hide before that point, re-checking later if not - this
  // can't get stuck the way a naive counter could, since the pin is always
  // a fixed clock time that a quiet page will eventually pass, never an
  // open-ended count waiting to reach zero.
  //
  // PAGE_OVERLAY_CLICK_PIN_MS is calibrated from ONE live sample on a local
  // Shopify CLI dev server (127.0.0.1) - click-to-real-refresh measured at
  // ~2.9s there, well above Dawn's 800ms debounce alone, almost certainly
  // because a local dev server's section-rendering fetch is much slower
  // than production's CDN-backed one. Set with margin above that observed
  // worst case; if real click-to-refresh timing on a live/staging URL turns
  // out to be much faster (likely), this can be shortened - erring long is
  // the safe direction (overlay stays open a bit longer than strictly
  // needed), where erring short reopens exactly this bug.
  var PAGE_OVERLAY_CLICK_PIN_MS = 3200;
  var pageOverlayPinnedUntil = 0;

  function showPageOverlay() {
    var el = ensurePageOverlay();
    pageOverlayGeneration += 1;
    pageOverlayShownAt = Date.now();
    el.style.opacity = '1';
    el.style.pointerEvents = 'auto';
  }

  function scheduleOverlayHideCheck(myGeneration, wait) {
    setTimeout(function () {
      if (pageOverlayGeneration !== myGeneration) return; // superseded by a newer show - a later hide call will handle it
      var remainingPin = pageOverlayPinnedUntil - Date.now();
      if (remainingPin > 0) {
        // A recent checkbox change means facets.js's own debounced submit
        // (and the fetch it triggers) is still expected to land - keep
        // waiting instead of hiding into that gap.
        scheduleOverlayHideCheck(myGeneration, remainingPin);
        return;
      }
      var current = document.getElementById(PAGE_OVERLAY_ID);
      if (!current) return;
      current.style.opacity = '0';
      current.style.pointerEvents = 'none';
    }, wait);
  }

  function hidePageOverlay() {
    var el = document.getElementById(PAGE_OVERLAY_ID);
    if (!el) return;
    var myGeneration = pageOverlayGeneration;
    var elapsed = pageOverlayShownAt ? (Date.now() - pageOverlayShownAt) : PAGE_OVERLAY_MIN_VISIBLE_MS;
    var wait = Math.max(0, PAGE_OVERLAY_MIN_VISIBLE_MS - elapsed) + PAGE_OVERLAY_SETTLE_MS;
    scheduleOverlayHideCheck(myGeneration, wait);
  }

  function ensureSpinner(container) {
    if (!container || container.querySelector('.' + SPINNER_CLASS)) return;
    var spinner = document.createElement('span');
    spinner.className = SPINNER_CLASS;
    spinner.setAttribute('aria-hidden', 'true');
    container.appendChild(spinner);
  }

  function removeSpinner(container) {
    if (!container) return;
    var spinner = container.querySelector('.' + SPINNER_CLASS);
    if (spinner) spinner.remove();
  }

  function getCheckboxCountElements(item) {
    var visible = [], hidden = [];
    item.elements.forEach(function (cb) {
      var label = cb.closest('label');
      if (!label) return;
      var text = label.querySelector('.facet-checkbox__text');
      var hiddenEl = label.querySelector('.visually-hidden');
      if (text) visible.push(text);
      if (hiddenEl) hidden.push(hiddenEl);
    });
    return { visible: visible, hidden: hidden };
  }

  function getOverallCountElements() {
    return Array.from(document.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count'));
  }

  function setDim(elements, isPending) { elements.forEach(function (el) { el.classList.toggle(PENDING_CLASS, isPending); }); }
  function setSpinner(elements, isPending) { elements.forEach(function (el) { if (isPending) { ensureSpinner(el); } else { removeSpinner(el); } }); }

  function markAllPending() {
    var map = collectCheckboxes();
    Object.keys(map).forEach(function (k) {
      var els = getCheckboxCountElements(map[k]);
      setDim(els.visible, true);
      setDim(els.hidden, true);
    });
    setDim(getOverallCountElements(), true);
    showPageOverlay();
  }

  function clearAllPending() {
    var map = collectCheckboxes();
    Object.keys(map).forEach(function (k) {
      var els = getCheckboxCountElements(map[k]);
      setDim(els.visible, false);
      setSpinner(els.visible, false);
      setDim(els.hidden, false);
    });
    setDim(getOverallCountElements(), false);
    hidePageOverlay();
  }

  function countElementsForCheckbox(cb) {
    var label = cb.closest('label');
    if (!label) return { visible: [], hidden: [] };
    var text = label.querySelector('.facet-checkbox__text');
    var hiddenEl = label.querySelector('.visually-hidden');
    return { visible: text ? [text] : [], hidden: hiddenEl ? [hiddenEl] : [] };
  }

  function markJustClicked(cb) {
    var els = countElementsForCheckbox(cb);
    setDim(els.visible, true);
    setSpinner(els.visible, true);
    setDim(els.hidden, true);
    setDim(getOverallCountElements(), true);
    showPageOverlay();
  }

  // ---- Main run loop ----

  async function run() {
    var grid = document.getElementById('product-grid');
    if (!grid || !grid.dataset.id) {
      // Bug fixed 2026-07-30: the MutationObserver below calls
      // markAllPending() (which shows the loading overlay) synchronously,
      // BEFORE this debounced run() even fires. Every other exit path here
      // calls clearAllPending() to hide it again - this one didn't. If the
      // observer fires for a mutation inside #ProductGridContainer that
      // isn't actually a grid re-render (or catches the grid mid-swap,
      // momentarily missing #product-grid or its dataset.id), run() bailed
      // here and the overlay was left showing forever, with no future
      // mutation guaranteed to come along and clear it.
      clearAllPending();
      return;
    }
    var sectionId = grid.dataset.id; // kept for signature compat; not used in Worker path

    markAllPending();

    var map = collectCheckboxes();
    var expectedPmfKeys = Array.from(
      new Set(
        Object.keys(map)
          .map(function (k) { return map[k]; })
          .filter(function (item) { return item.kind === 'pmf'; })
          .map(function (item) { return item.entryKey; })
      )
    );

    var catalog = await fetchFullCatalog(sectionId, expectedPmfKeys);
    if (!catalog) {
      clearAllPending();
      return;
    }

    // Bug fixed 2026-08-13 (6): everything from here through the end of
    // run() is now wrapped in try/finally. run() is an async function that
    // nobody awaits or attaches a .catch to (it's invoked fire-and-forget
    // from DOMContentLoaded/the MutationObserver callback below) - if
    // ANYTHING in this block throws, the exception was previously silently
    // swallowed by the JS engine and execution just stopped right there,
    // skipping the clearAllPending() call at the end and leaving the
    // full-page loading overlay (showPageOverlay(), turned on by
    // markAllPending() above) stuck on screen forever. That's exactly what
    // happened when the 2026-08-13 (5) re-collectCheckboxes() fix below was
    // first deployed: on some page loads, re-querying the DOM immediately
    // after a native Shopify re-render hit a shape this code hadn't been
    // exercised against, threw, and the overlay never got dismissed. The
    // fix itself (re-reading live checkbox references after the catalog
    // await) is still correct and still needed - see its comment - but it,
    // and everything after it, must never be able to leave the page stuck
    // behind a permanent overlay. try/finally guarantees clearAllPending()
    // always runs, whether this block finishes normally or throws.
    try {
    // Bug fixed 2026-08-13 (5): `map` above was captured BEFORE this await -
    // on a cold page load, fetchFullCatalog() can take several hundred ms
    // (first-ever fetch from the Worker, no sessionStorage cache yet), and
    // Shopify's own facets.js does its own native DOM hydration during that
    // same window (confirmed live: a MutationObserver firing mid-fetch,
    // before the catalog had even resolved). If that native render replaces
    // the checkbox/label elements (the same outerHTML-swap mechanism as the
    // 2026-08-13 (3) fix above), `map`'s `item.elements` end up pointing at
    // now-detached nodes. Every count below is still computed correctly
    // (computation only depends on the catalog and the label/value strings
    // already captured, not on live DOM), and updateCheckboxCount() still
    // "succeeds" - it just writes onto elements nobody can see anymore,
    // while the screen keeps showing Shopify's original native, page-scoped
    // count. That's exactly the reported symptom: correct numbers if you
    // inspect the computation, wrong numbers on screen, until any later
    // native re-render (e.g. the one a click triggers) is followed by our
    // MutationObserver re-collecting fresh nodes. Re-collecting here, after
    // the await and immediately before the synchronous patch loops below
    // (nothing yields between here and the end of run(), so no further
    // native render can interleave once this line runs), closes that
    // window down to effectively zero. Uses the same collectCheckboxes()
    // function, so it's still the exact same identification of kind/key/
    // label/value/checked - only the DOM element references get refreshed.
    map = collectCheckboxes();

    var activeEntries = activeEntriesFromCheckboxes(map);
    var totalCount = activeEntries.length ? countMatches(catalog, activeEntries) : catalog.length;
    updateOverallCount(totalCount);

    var groups = {};
    var checkedGroups = {};
    Object.keys(map).forEach(function (k) {
      var item = map[k];
      if (item.checked) {
        var checkedGroupKey = item.kind + '::' + item.entryKey;
        if (!checkedGroups[checkedGroupKey]) checkedGroups[checkedGroupKey] = { kind: item.kind, entryKey: item.entryKey, items: [] };
        checkedGroups[checkedGroupKey].items.push(item);
        return;
      }
      var groupKey = item.kind + '::' + item.entryKey;
      if (!groups[groupKey]) groups[groupKey] = { kind: item.kind, entryKey: item.entryKey, items: [] };
      groups[groupKey].items.push(item);
    });

    // Bug fixed 2026-08-12 (4): every CHECKED value in a filter used to get
    // patched with the same shared totalCount - the union across every
    // checked value in that filter, OR-combined. That's correct when only
    // ONE value of a filter is checked (the union of one value IS that
    // value's own count), but wrong as soon as two or more values of the
    // SAME filter are checked at once (e.g. Product Type: Sunglasses +
    // Underwear both checked) - both boxes showed the shared total (19 = 3
    // + 16) instead of their own numbers (3 and 16). Mirrors the matching
    // fix in facets.liquid: once a filter has more than one active value,
    // each active value is re-tested on its own, combined with every OTHER
    // filter's active entries, but never unioned with its own checked
    // siblings - the same principle as an unchecked candidate preview,
    // just applied to the checked ones too, and for every kind (pmf,
    // option, vmf) since none of them should let a checked sibling bleed
    // into another value's own count.
    Object.keys(checkedGroups).forEach(function (checkedGroupKey) {
      var checkedGroup = checkedGroups[checkedGroupKey];
      if (checkedGroup.items.length <= 1) {
        checkedGroup.items.forEach(function (item) {
          updateCheckboxCount(item, totalCount);
          setZeroCountVisibility(item, totalCount);
        });
        return;
      }
      var otherCheckedEntries = activeEntries.filter(function (e) {
        return !(e.kind === checkedGroup.kind && e.key === checkedGroup.entryKey);
      });
      checkedGroup.items.forEach(function (item) {
        // Bug fixed 2026-08-13: built as a plain label string for
        // non-pmf kinds before - now always {label, fuzzy}, same reason as
        // the activeEntriesFromCheckboxes() change above.
        var soloEntry = {
          kind: checkedGroup.kind,
          key: checkedGroup.entryKey,
          values: [{ label: item.label, fuzzy: isGroupedFilterValue(item.value) }]
        };
        var soloCount = countMatches(catalog, otherCheckedEntries.concat([soloEntry]));
        updateCheckboxCount(item, soloCount);
        setZeroCountVisibility(item, soloCount);
      });
    });

    Object.keys(groups).forEach(function (groupKey) {
      var group = groups[groupKey];
      var otherEntries = activeEntries.filter(function (e) {
        return !(e.kind === group.kind && e.key === group.entryKey);
      });

      // Only PMF (product-level) other-entries are safe to pre-filter with
      // independently - they don't depend on which variant matched. Any
      // OTHER variant-level entries (option/vmf, e.g. an active Size
      // filter) must be combined with the candidate value into a single
      // matching pass below, so one variant satisfies all of them at once -
      // see the 2026-07-29 fix note in the file header for why.
      var otherPmfEntries = otherEntries.filter(function (e) { return e.kind === 'pmf'; });
      var otherVariantEntries = otherEntries.filter(function (e) { return e.kind !== 'pmf'; });
      var eligible = otherPmfEntries.length
        ? catalog.filter(function (p) { return productMatchesEntries(p, otherPmfEntries); })
        : catalog;

      group.items.forEach(function (item) {
        // Bug fixed 2026-08-12 (2): for a product-level (pmf) filter,
        // previewing an unchecked value while a SIBLING value of that SAME
        // filter is already checked (e.g. Product Type: Underwear checked,
        // previewing "Tops and Tees") used to union the candidate with
        // every already-active sibling value before counting - showing "how
        // many products if you ALSO checked this" instead of this value's
        // own standalone count. Shopify's own native filter.values[].count
        // for a pmf value doesn't shrink or grow based on other checked
        // values in the same filter (confirmed via the debug banner:
        // Trousers/Tops and Tees kept their original native counts - 89/160
        // - even with Underwear checked).
        //
        // Bug fixed 2026-08-12 (6): the same union was still happening for
        // OPTION/VMF filters (e.g. Fit, Size) - only pmf had been fixed
        // above. Evidence: with Fit "Regular" (145) checked and previewing
        // unchecked "Tall", the box showed (145) - identical to Regular's
        // own count, because Tall (36) turned out to be a near-total subset
        // of Regular, so union(Regular, Tall) == 145. That preview promised
        // "145 products if you also check this", but checking Tall actually
        // shows (36) - its own standalone count, per the 2026-08-12 (4) fix
        // above, which deliberately made every CHECKED value show its own
        // number rather than the shared union. Previewing a union number
        // that the box then abandons the instant it's checked is exactly
        // the same inconsistency the pmf fix above already solved, just for
        // a different kind - so the same fix applies: test the candidate
        // value alone, never unioned with its own checked siblings, so the
        // number shown before checking a box matches the number it keeps
        // after checking it. The overall "X products" total (updateOverallCount
        // above) still correctly reflects the true OR-union of every checked
        // value once a box IS checked - only this per-box preview changes.
        // Bug fixed 2026-08-13: built as a plain label string for non-pmf
        // kinds before - now always {label, fuzzy}, same reason as the
        // activeEntriesFromCheckboxes() change above.
        var candidateValues = [{ label: item.label, fuzzy: isGroupedFilterValue(item.value) }];
        var combinedEntries = otherVariantEntries.concat([
          { kind: group.kind, key: group.entryKey, values: candidateValues }
        ]);
        var count = countMatches(eligible, combinedEntries);
        updateCheckboxCount(item, count);
        setZeroCountVisibility(item, count);
      });
    });
    } finally {
      clearAllPending();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  document.addEventListener('change', function (e) {
    var cb = e.target;
    if (!cb || cb.tagName !== 'INPUT' || cb.type !== 'checkbox') return;
    if (!cb.closest('#FacetFiltersForm') && !cb.closest('#FacetFiltersFormMobile')) return;
    // Bug fixed 2026-08-13 (11): pin the overlay open through facets.js's own
    // debounced submit + fetch window - see the fix note on
    // PAGE_OVERLAY_CLICK_PIN_MS above for why this is measured from the
    // click itself rather than from any one run() cycle's own completion.
    pageOverlayPinnedUntil = Date.now() + PAGE_OVERLAY_CLICK_PIN_MS;
    markJustClicked(cb);
  });

  // Bug fixed 2026-08-13 (3): this only ever watched #ProductGridContainer.
  // But Shopify's own facets.js (renderFilters()) rewrites the raw HTML of
  // EVERY filter group on EVERY filter change - the group you just clicked
  // via renderCounts()/renderMobileCounts() (outerHTML swap), every OTHER
  // group via a plain innerHTML swap - and that all happens inside
  // #main-collection-filters, which is a SIBLING of #ProductGridContainer,
  // not a descendant (confirmed directly in
  // main-collection-product-grid.liquid: the <aside id="main-collection-
  // filters"> closes before <div id="ProductGridContainer"> even opens).
  // Every one of those native rewrites re-injects Shopify's own, uncorrected
  // counts - exactly what this whole file exists to correct - but only the
  // grid swap (a separate, later step in the same renderFilters/
  // renderProductGridContainer sequence) was ever visible to this observer.
  // Confirmed live: with Size:Large + Fit:Petite both active, Fit's own
  // checkbox (the one just clicked, corrected via the change-listener path
  // below) showed the correct combined count (4), while Size's checkbox -
  // rewritten with fresh native HTML by the *other-groups* branch of
  // renderFilters(), a mutation this observer never saw - stayed stuck on
  // whatever Shopify natively rendered (12), with nothing left to trigger a
  // re-patch. Watching the filters aside too closes that gap: any native
  // rewrite there now retriggers the same debounced run() as a grid change
  // does, so every group gets re-corrected no matter which one Shopify just
  // rewrote.
  var wrapper = document.getElementById('ProductGridContainer');
  // Feature added 2026-08-17: the search results template (sections/main-
  // search.liquid, which now also loads this script) uses its own filters
  // aside id, "main-search-filters", not "main-collection-filters" - the two
  // templates share #ProductGridContainer's id but not this one. Checking
  // both (only one will ever exist on a given page) keeps this working on
  // both templates without needing to know which one rendered the page.
  var filtersAside = document.getElementById('main-collection-filters') || document.getElementById('main-search-filters');
  if (wrapper || filtersAside) {
    var pending = null;
    var observer = new MutationObserver(function () {
      markAllPending();
      clearTimeout(pending);
      pending = setTimeout(run, 50);
    });
    if (wrapper) observer.observe(wrapper, { childList: true, subtree: true });
    if (filtersAside) observer.observe(filtersAside, { childList: true, subtree: true });
  }
})();